"use client";
import dynamic from "next/dynamic";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import Indicator from "@/components/CustomUI/modules/Loaders/Indicator";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import {
  getURLSearchParamsEntries,
  createParamsStringFromFilters,
} from "@/lib/utils";
import { applyFiltersAPI } from "@/clientSideApi/catalog";
const Container = dynamic(() => import("./Container"), { ssr: false });

const Filters = ({ categorySlug, data, metaData }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const timerRef = useRef();
  const defaultAttributes =
    useSelector((state) => state.catalog?.data?.new_attributes) || data;

  const defaultFilters = useMemo(
    () => getURLSearchParamsEntries(searchParams),
    [searchParams]
  );

  const hasFilters = useMemo(() => {
    return (
      Object.keys(defaultFilters).filter(
        (ele) => ele !== "sort_by" && ele !== "page"
      ).length > 0
    );
  }, [defaultFilters]);

  const [open, setOpen] = useState(false);
  const [attributes, setAttributes] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [change, setChange] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(false);

  const min_price = Math.round((metaData?.min_price || 0) / 1000) * 1000;
  const max_price = Math.round((metaData?.max_price || 0) / 1000) * 1000;

  const checkChange = useCallback((originalFilters, currentFilters) => {
    const currentKeys = Object.keys(currentFilters);
    const originalKeys = Object.keys(originalFilters);
    if (originalKeys.length !== currentKeys.length) return true;
    for (const element of currentKeys) {
      if (!originalKeys.includes(element)) {
        return true;
      }
    }
    for (const key of currentKeys) {
      const ogData = originalFilters[key];
      const newData = currentFilters[key];
      if (ogData.length !== newData.length) return true;
      for (const element of ogData) {
        if (!newData.includes(element)) return true;
      }
    }
    return false;
  }, []);

  const callAPI = async (updatedFilters, categorySlug) => {
    const searchParams = createParamsStringFromFilters(updatedFilters);
    const apiResponse = await applyFiltersAPI(categorySlug, searchParams);
    if (
      apiResponse &&
      apiResponse.new_attributes &&
      Object.keys(apiResponse.new_attributes).length > 0
    ) {
      setAttributes(apiResponse.new_attributes);
    }
    setLoading(false);
  };

  const onOpenChange = useCallback(
    (val) => {
      setOpen(val);
      if (val) {
        setAttributes(defaultAttributes);
        setSelectedFilters(defaultFilters);
        setChange(false);
        setLoading(false);
        setCurrentPrice([min_price, max_price]);
        setPriceChange(false);
      }
    },
    [defaultFilters, defaultAttributes, min_price, max_price]
  );

  const handlePriceChange = useCallback(
    (val) => {
      if (loading) return false;
      clearTimeout(timerRef.current);
      const new_min_price = Math.round(val[0] / 1000) * 1000;
      const new_max_price = Math.round(val[1] / 1000) * 1000;
      setCurrentPrice([new_min_price, new_max_price]);
      const updatedFilters = { ...selectedFilters };
      updatedFilters["price_0"] = [new_min_price];
      updatedFilters["price_1"] = [new_max_price];
      timerRef.current = setTimeout(() => {
        setLoading(true);
        callAPI(updatedFilters, categorySlug);
        setPriceChange(
          new_min_price !== min_price || new_max_price !== max_price
        );
      }, 200);
    },
    [selectedFilters, loading, categorySlug, timerRef, max_price, min_price]
  );

  const handlePress = useCallback(
    (filter, selected) => {
      if (loading) return;
      setLoading(true);
      const updatedFilters = { ...selectedFilters };
      if (selected) {
        const temp = updatedFilters[filter.attrib_slug].filter(
          (ele) => ele !== filter.attrib_value_slug
        );
        if (temp.length > 0) {
          updatedFilters[filter.attrib_slug] = temp;
        } else {
          delete updatedFilters[filter.attrib_slug];
        }
        setSelectedFilters(updatedFilters);
      } else {
        if (updatedFilters.hasOwnProperty(filter.attrib_slug)) {
          updatedFilters[filter.attrib_slug].push(filter.attrib_value_slug);
        } else {
          updatedFilters[filter.attrib_slug] = [filter.attrib_value_slug];
        }
        setSelectedFilters(updatedFilters);
      }
      setChange(checkChange(defaultFilters, updatedFilters));
      callAPI(updatedFilters, categorySlug);
      setChange(checkChange(defaultFilters, updatedFilters));
    },
    [
      loading,
      selectedFilters,
      defaultFilters,
      categorySlug,
      checkChange,
    ]
  );

  const removeCategory = useCallback(
    (category) => {
      if (loading) return;
      setLoading(true);
      const isPrice = /^price_[01]$/.test(category);
      const updatedFilters = { ...selectedFilters };
      if (!isPrice) {
        delete updatedFilters[category];
      } else {
        if (updatedFilters.hasOwnProperty("price_0")) {
          delete updatedFilters["price_0"];
        }
        if (updatedFilters.hasOwnProperty("price_1")) {
          delete updatedFilters["price_1"];
        }
      }
      setSelectedFilters(updatedFilters);
      setChange(true);
      callAPI(updatedFilters, categorySlug);
      if (isPrice) setCurrentPrice(null);
    },
    [selectedFilters, categorySlug, loading]
  );

  const closeOtherAccordian = useCallback((key) => {
    const accordian = document.querySelector(".filter-" + key);
    if (accordian) {
      const dataState = accordian.children[0]?.getAttribute("data-state");
      if (dataState && dataState === "open") {
        const button = accordian.querySelector("[type=button]");
        if (button) button.click();
      }
    }
  }, []);

  const applyFilters = () => {
    if (change || priceChange) {
      const finalFilters = { ...selectedFilters };
      if (currentPrice && priceChange) {
        finalFilters["price_0"] = currentPrice[0];
        finalFilters["price_1"] = currentPrice[1];
      }
      router.push(pathname + "?" + createParamsStringFromFilters(finalFilters));
    }
    onOpenChange(false);
  };

  useEffect(() => {
    setAttributes(defaultAttributes);
    setSelectedFilters(defaultFilters);
    setChange(false);
    setLoading(false);
  }, [defaultAttributes, defaultFilters]);

  if (!metaData || !metaData.total_items) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetTrigger
        asChild
        aria-controls=""
      >
        <Button
          aria-label="Filter"
          variant="secondary"
          className="
              relative border-[0.5px] border-[#393939] h-[30px] 
              md:min-w-[120px] text-xs px-3 md:bg-[#F0FAFA]
              text-font-primaryLight bg-white hover:drop-shadow
            "
        >
          Filter
          <Indicator show={hasFilters} />
        </Button>

      </SheetTrigger>

      <Container
        loading={loading}
        min_price={min_price}
        max_price={max_price}
        selectedFilters={selectedFilters}
        currentPrice={currentPrice}
        attributes={attributes}
        hasChange={change || priceChange}
        handlePriceChange={handlePriceChange}
        removeCategory={removeCategory}
        closeOtherAccordian={closeOtherAccordian}
        handlePress={handlePress}
        applyFilters={applyFilters}
      />
    </Sheet>
  );
};

export default Filters;
